import { useEffect, useState } from 'react';
import { Modal } from '../../../components/Modal';
import Button from '../../../components/Button';
import { journeyService } from '../services/journeyService';
import type { JourneySectionOption } from '../services/journeyTypes';

interface MoveJourneyItemModalProps {
    currentSectionId: string;
    onMove: (targetChapterId: string, targetSectionId: string) => void | Promise<void>;
    onCancel: () => void;
}

interface ChapterGroup {
    chapterId: string;
    label: string;
    sections: JourneySectionOption[];
}

export const MoveJourneyItemModal = ({ currentSectionId, onMove, onCancel }: MoveJourneyItemModalProps) => {
    const [options, setOptions] = useState<JourneySectionOption[]>([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [selectedSectionId, setSelectedSectionId] = useState('');
    const [isMoving, setIsMoving] = useState(false);

    useEffect(() => {
        let active = true;
        (async () => {
            try {
                const allOptions = await journeyService.getAllSectionOptions();
                if (active) {
                    setOptions(allOptions.filter((option) => option.sectionId !== currentSectionId));
                }
            } catch (err) {
                if (active) {
                    setError(err instanceof Error ? err.message : 'Failed to load sections');
                }
            } finally {
                if (active) {
                    setLoading(false);
                }
            }
        })();
        return () => {
            active = false;
        };
    }, [currentSectionId]);

    const chapterGroups: ChapterGroup[] = [];
    for (const option of options) {
        let group = chapterGroups.find((g) => g.chapterId === option.chapterId);
        if (!group) {
            group = {
                chapterId: option.chapterId,
                label: `Chapter ${option.chapterNumber}: ${option.chapterTitle || 'Untitled'}`,
                sections: [],
            };
            chapterGroups.push(group);
        }
        group.sections.push(option);
    }

    const handleConfirm = async () => {
        const target = options.find((option) => option.sectionId === selectedSectionId);
        if (!target) return;
        setIsMoving(true);
        try {
            await onMove(target.chapterId, target.sectionId);
        } finally {
            setIsMoving(false);
        }
    };

    return (
        <Modal isOpen onClose={onCancel} title="Move Item to Section" size="md">
            <div className="space-y-4">
                {loading && (
                    <div className="flex items-center justify-center py-8">
                        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600" />
                    </div>
                )}

                {!loading && error && <p className="text-sm text-red-600">{error}</p>}

                {!loading && !error && (
                    <div>
                        <label htmlFor="move-target-section" className="block text-sm font-medium text-gray-700 mb-1">
                            Target section
                        </label>
                        <select
                            id="move-target-section"
                            value={selectedSectionId}
                            onChange={(e) => setSelectedSectionId(e.target.value)}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        >
                            <option value="">Select a section…</option>
                            {chapterGroups.map((group) => (
                                <optgroup key={group.chapterId} label={group.label}>
                                    {group.sections.map((section) => (
                                        <option key={section.sectionId} value={section.sectionId}>
                                            {section.sectionHeader || 'Untitled section'}
                                        </option>
                                    ))}
                                </optgroup>
                            ))}
                        </select>
                        {chapterGroups.length === 0 && (
                            <p className="text-sm text-gray-500 mt-2">No other sections available.</p>
                        )}
                    </div>
                )}

                <div className="flex justify-end gap-3 pt-4 border-t border-gray-200">
                    <Button onClick={onCancel} variant="secondary">
                        Cancel
                    </Button>
                    <Button
                        onClick={handleConfirm}
                        variant="primary"
                        loading={isMoving}
                        disabled={isMoving || !selectedSectionId}
                    >
                        Move
                    </Button>
                </div>
            </div>
        </Modal>
    );
};
